<template>
  <div class="referrals-crud">
    <div class="d-flex justify-space-between align-center flex-wrap my-4">
      <h3>إدارة الروابط الديناميكية</h3>
      <div class="d-flex align-center">
        <v-btn
          v-if="selected.length"
          color="error"
          class="ml-2"
          @click="openBulkArchiveDialog"
        >
          <v-icon left>mdi-archive</v-icon>
          أرشفة المحدد ({{ selected.length }})
        </v-btn>
        <v-btn color="primary" @click="openAddDialog">
          <v-icon left>mdi-plus</v-icon>
          إضافة رابط ديناميكي
        </v-btn>
      </div>
    </div>

    <v-card class="mb-4">
      <v-card-text class="d-flex flex-wrap align-center" style="gap: 12px">
        <v-text-field
          v-model="filters.name"
          label="البحث بالإسم أو الكود"
          dense
          outlined
          hide-details
          clearable
          style="max-width: 260px"
          @keyup.enter="applyFilters"
        />
        <v-text-field
          v-model="filters.url"
          label="البحث بالرابط"
          dense
          outlined
          hide-details
          clearable
          style="max-width: 260px"
          @keyup.enter="applyFilters"
        />
        <v-autocomplete
          v-model="filters.brand"
          :items="brandNames"
          label="البراند"
          multiple
          small-chips
          deletable-chips
          dense
          outlined
          hide-details
          clearable
          style="max-width: 240px"
        />
        <v-autocomplete
          v-model="filters.platform"
          :items="platformNames"
          label="المنصة"
          multiple
          small-chips
          deletable-chips
          dense
          outlined
          hide-details
          clearable
          style="max-width: 240px"
        />
        <v-select
          v-model="filters.is_google_sheet"
          :items="sheetFilterOptions"
          item-text="label"
          item-value="value"
          label="النوع"
          dense
          outlined
          hide-details
          style="max-width: 180px"
        />
        <v-btn-toggle v-model="filters.status" mandatory dense>
          <v-btn :value="'all'">الكل</v-btn>
          <v-btn :value="1">نشطين</v-btn>
          <v-btn :value="0">مؤرشفين</v-btn>
        </v-btn-toggle>
        <v-btn color="primary" @click="applyFilters">تطبيق</v-btn>
      </v-card-text>
    </v-card>

    <v-card>
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="referrals"
        :loading="loading"
        :server-items-length="total"
        :items-per-page="perPage"
        :page.sync="currentPage"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        item-key="id"
        show-select
        class="elevation-1"
        @update:page="fetchReferrals"
        @update:items-per-page="onPerPageChange"
      >
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="item.status === 1 ? 'success' : 'error'" small>
            {{ item.status === 1 ? "نشط" : "مؤرشف" }}
          </v-chip>
        </template>

        <template v-slot:[`item.agents`]="{ item }">
          <v-chip
            v-for="agent in item.agents || []"
            :key="agent.id"
            x-small
            color="primary"
            class="ma-1"
          >
            {{ agent.name }}
          </v-chip>
          <span v-if="!item.agents || !item.agents.length" class="error--text">
            لم يتم تحديد استشاريين
          </span>
        </template>

        <template v-slot:[`item.website`]="{ item }">
          <span dir="ltr">{{ item.website }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" small icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="openEditDialog(item)">
                <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                <v-list-item-title>تعديل</v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="copyUrl(fullReferralUrl(item), 'الرابط')"
                :disabled="item.is_google_sheet || !item.url1"
              >
                <v-list-item-icon><v-icon>mdi-content-copy</v-icon></v-list-item-icon>
                <v-list-item-title>نسخ الرابط</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.status === 1" @click="openArchiveDialog(item)">
                <v-list-item-icon><v-icon>mdi-archive</v-icon></v-list-item-icon>
                <v-list-item-title>أرشفة</v-list-item-title>
              </v-list-item>
              <v-list-item v-else @click="openRestoreDialog(item)">
                <v-list-item-icon><v-icon>mdi-restore</v-icon></v-list-item-icon>
                <v-list-item-title>إستعادة</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openDeleteDialog(item)">
                <v-list-item-icon><v-icon color="error">mdi-delete-forever</v-icon></v-list-item-icon>
                <v-list-item-title class="error--text">حذف نهائي</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add / Edit Dialog -->
    <v-dialog v-model="dialog" max-width="720px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">
            {{ editMode ? "تعديل رابط ديناميكي" : "إضافة رابط ديناميكي" }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12">
                <v-checkbox
                  v-model="form.is_google_sheet"
                  label="استيراد من Google Sheet"
                  hide-details
                  dense
                  class="mt-0"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  label="إسم الحملة"
                  :rules="[rules.required]"
                  outlined
                  dense
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.username"
                  label="كود الحملة / الرابط"
                  :rules="form.is_google_sheet ? [] : [rules.required]"
                  :disabled="form.is_google_sheet"
                  outlined
                  dense
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.website"
                  :items="websiteOptions"
                  item-text="label"
                  item-value="value"
                  label="الموقع"
                  :rules="[rules.required]"
                  outlined
                  dense
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-combobox
                  v-model="form.brand"
                  :items="brandNames"
                  label="البراند"
                  hint="يمكنك إضافة براند جديد"
                  persistent-hint
                  :rules="[rules.required]"
                  outlined
                  dense
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-combobox
                  v-model="form.platform"
                  :items="platformNames"
                  label="المنصة"
                  hint="يمكنك إضافة منصة جديدة"
                  persistent-hint
                  :rules="[rules.required]"
                  outlined
                  dense
                  required
                />
              </v-col>
              <v-col v-if="form.is_google_sheet" cols="12" md="6">
                <v-combobox
                  v-model="form.haml_source"
                  :items="hamlSourceNames"
                  label="مصدر الحملة (haml_source)"
                  hint="يمكنك إضافة مصدر جديد"
                  persistent-hint
                  :rules="[rules.required]"
                  outlined
                  dense
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.salesagents"
                  :items="salesAgents"
                  item-text="name"
                  item-value="id"
                  label="الإستشاريين"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.note"
                  label="الملاحظة"
                  rows="2"
                  outlined
                  dense
                />
              </v-col>
              <v-col v-if="!form.is_google_sheet" cols="12">
                <div class="d-flex align-center" style="gap: 8px" dir="ltr">
                  <v-text-field
                    :value="sitePrefix"
                    label="الموقع"
                    outlined
                    dense
                    disabled
                    hide-details
                    style="direction: ltr; flex: 1 1 60%"
                  />
                  <v-text-field
                    v-model="form.url1"
                    label="لاحقة الرابط (URL suffix)"
                    :rules="[rules.required, rules.slug]"
                    outlined
                    dense
                    style="direction: ltr; flex: 1 1 40%"
                    placeholder="summer-2026"
                  />
                </div>
                <div class="text-caption mt-1" dir="ltr" style="word-break: break-all">
                  <span class="grey--text">الرابط النهائي: </span>
                  <span v-if="form.website && form.url1">{{ sitePrefix + form.url1 }}</span>
                  <span v-else class="grey--text">—</span>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">إلغاء</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveReferral">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Archive Dialog -->
    <v-dialog v-model="archiveDialog" max-width="420px" persistent>
      <v-card>
        <v-card-title class="headline">هل انت متأكد من أرشفة الرابط؟</v-card-title>
        <v-card-text>
          لن يتمكن المستخدمين من استخدام رابط الحملة وسوف يتم توجيههم لصفحة اطلب
          تمويل الخاصة بالوساطة.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="archiveDialog = false">إلغاء</v-btn>
          <v-btn color="error" :loading="actionLoading" @click="confirmArchive">أرشفة</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Restore Dialog -->
    <v-dialog v-model="restoreDialog" max-width="420px" persistent>
      <v-card>
        <v-card-title class="headline">هل انت متأكد من استعادة الرابط؟</v-card-title>
        <v-card-text>سوف يتمكن المستخدمين من استخدام الرابط.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="restoreDialog = false">إلغاء</v-btn>
          <v-btn color="success" :loading="actionLoading" @click="confirmRestore">
            نعم استعادة الرابط
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Permanently Dialog -->
    <v-dialog v-model="deleteDialog" max-width="440px" persistent>
      <v-card>
        <v-card-title class="headline error--text">هل انت متأكد من الحذف النهائي؟</v-card-title>
        <v-card-text>
          سيتم حذف الرابط نهائياً ولا يمكن التراجع عن هذه العملية.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" :loading="actionLoading" @click="confirmDelete">
            حذف نهائي
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Archive Dialog -->
    <v-dialog v-model="bulkArchiveDialog" max-width="420px" persistent>
      <v-card>
        <v-card-title class="headline">
          أرشفة {{ selected.length }} رابط
        </v-card-title>
        <v-card-text>هل أنت متأكد من أرشفة الروابط المحددة؟</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="bulkArchiveDialog = false">إلغاء</v-btn>
          <v-btn color="error" :loading="actionLoading" @click="confirmBulkArchive">
            أرشفة
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Axios from "axios";
import { buildReferralUrl, referralSitePrefix } from "@/utils/referralUrl";

export default {
  name: "ReferralsCrudIndex",
  data() {
    return {
      loading: false,
      saving: false,
      actionLoading: false,
      referrals: [],
      total: 0,
      perPage: 10,
      currentPage: 1,
      selected: [],
      filters: {
        name: "",
        url: "",
        status: "all",
        brand: [],
        platform: [],
        is_google_sheet: "all",
      },
      sheetFilterOptions: [
        { label: "الكل", value: "all" },
        { label: "Google Sheet", value: 1 },
        { label: "روابط عادية", value: 0 },
      ],
      dialog: false,
      editMode: false,
      valid: false,
      form: this.blankForm(),
      archiveDialog: false,
      restoreDialog: false,
      deleteDialog: false,
      bulkArchiveDialog: false,
      targetItem: null,
      brandNames: [],
      platformNames: [],
      hamlSourceNames: [],
      salesAgents: [],
      websiteOptions: [
        { label: "الوساطة", value: "https://alwsata.com.sa" },
        { label: "سيولة", value: "https://suyulasa.com" },
        { label: "الحاسبة", value: "https://hasbah.net" },
        { label: "نمو", value: "https://nomoufinance.com" },
      ],
      headers: [
        { text: "إسم الحملة", value: "name" },
        { text: "كود الحملة / الرابط", value: "username" },
        { text: "الموقع", value: "website" },
        { text: "المنصة", value: "platform" },
        { text: "البراند", value: "brand" },
        { text: "الإستشاريين", value: "agents", sortable: false },
        { text: "الحالة", value: "status" },
        { text: "العمليات", value: "actions", sortable: false },
      ],
      rules: {
        required: (v) =>
          (v !== null && v !== undefined && String(v).trim() !== "") ||
          "هذا الحقل مطلوب",
        slug: (v) =>
          !v ||
          /^[A-Za-z0-9\-_]+$/.test(v) ||
          "اللاحقة تحتوي على أحرف إنجليزية وأرقام وشرطة فقط",
      },
    };
  },
  computed: {
    apiBase() {
      return `${this.$store.state.url}/employee/admin/referrals-crud`;
    },
    sitePrefix() {
      return referralSitePrefix(this.form.website);
    },
  },
  watch: {
    "form.haml_source"(val) {
      if (this.form.is_google_sheet) this.form.username = val || "";
    },
    "form.is_google_sheet"(val) {
      if (val) {
        this.form.username = this.form.haml_source || "";
        this.form.url1 = "";
      }
    },
  },
  methods: {
    blankForm() {
      return {
        id: null,
        name: "",
        username: "",
        website: "",
        brand: "",
        platform: "",
        note: "",
        url1: "",
        is_google_sheet: false,
        haml_source: "",
        salesagents: [],
      };
    },
    fullReferralUrl(item) {
      if (!item) return "";
      return buildReferralUrl(item.website, item.url1);
    },
    buildBody() {
      const body = new FormData();
      if (this.filters.name) body.append("name", this.filters.name);
      if (this.filters.url) body.append("url", this.filters.url);
      if (this.filters.status !== "all" && this.filters.status !== null) {
        body.append("status", this.filters.status);
      }
      (this.filters.brand || []).forEach((b, i) => body.append(`brand[${i}]`, b));
      (this.filters.platform || []).forEach((p, i) => body.append(`platform[${i}]`, p));
      if (this.filters.is_google_sheet !== "all" && this.filters.is_google_sheet !== null) {
        body.append("is_google_sheet", this.filters.is_google_sheet);
      }
      body.append("per_page", this.perPage);
      return body;
    },
    async fetchReferrals() {
      this.loading = true;
      try {
        const res = await Axios.post(
          `${this.apiBase}?page=${this.currentPage}`,
          this.buildBody(),
          { headers: this.request_headers() }
        );
        this.referrals = res.data.payload.data || [];
        this.total = res.data.payload.count || 0;
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ");
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.selected = [];
      this.fetchReferrals();
    },
    onPerPageChange(v) {
      this.perPage = v;
      this.currentPage = 1;
      this.fetchReferrals();
    },
    async fetchMeta() {
      try {
        const [brands, platforms, hamlSources, agents] = await Promise.all([
          Axios.get(`${this.apiBase}/meta/brands`, { headers: this.request_headers() }),
          Axios.get(`${this.apiBase}/meta/platforms`, { headers: this.request_headers() }),
          Axios.get(`${this.apiBase}/meta/haml-sources`, { headers: this.request_headers() }),
          Axios.get(`${this.apiBase}/meta/sales-agents`, { headers: this.request_headers() }),
        ]);
        this.brandNames = (brands.data.payload || []).map((b) => b.name);
        this.platformNames = (platforms.data.payload || []).map((p) => p.name);
        this.hamlSourceNames = (hamlSources.data.payload || []).map((h) => h.name);
        this.salesAgents = agents.data.payload || [];
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "تعذر تحميل القوائم");
      }
    },
    openAddDialog() {
      this.editMode = false;
      this.form = this.blankForm();
      this.dialog = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    openEditDialog(item) {
      this.editMode = true;
      this.form = {
        id: item.id,
        name: item.name || "",
        username: item.username || "",
        website: item.website || "",
        brand: item.brand || "",
        platform: item.platform || "",
        note: item.note || "",
        url1: item.url1 || "",
        is_google_sheet: !!item.is_google_sheet,
        haml_source: item.haml_source || "",
        salesagents: (item.agents || []).map((a) => a.user_id),
      };
      this.dialog = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    closeDialog() {
      this.dialog = false;
      this.form = this.blankForm();
    },
    async saveReferral() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const body = new FormData();
        body.append("name", this.form.name);
        body.append("website", this.form.website);
        body.append("brand", this.form.brand);
        body.append("platform", this.form.platform);
        body.append("is_google_sheet", this.form.is_google_sheet ? 1 : 0);
        if (this.form.is_google_sheet) {
          body.append("haml_source", this.form.haml_source);
          // code mirrors haml_source; send it so uniqueness is validated cleanly
          body.append("username", this.form.haml_source);
        } else {
          body.append("username", this.form.username);
          if (this.form.url1) body.append("url1", this.form.url1);
        }
        if (this.form.note) body.append("note", this.form.note);
        (this.form.salesagents || []).forEach((id, i) => {
          body.append(`salesagents[${i}]`, id);
        });

        const url = this.editMode
          ? `${this.apiBase}/${this.form.id}/update`
          : `${this.apiBase}/store`;

        await Axios.post(url, body, { headers: this.request_headers() });

        this.showPopup("success", this.editMode ? "تم التعديل بنجاح" : "تمت الإضافة بنجاح");
        this.closeDialog();
        this.fetchReferrals();
      } catch (e) {
        const data = e.response?.data;
        const firstError =
          data?.errors && Object.values(data.errors)[0]?.[0];
        this.showPopup("error", firstError || data?.message || "حدث خطأ");
      } finally {
        this.saving = false;
      }
    },
    openArchiveDialog(item) {
      this.targetItem = item;
      this.archiveDialog = true;
    },
    async confirmArchive() {
      this.actionLoading = true;
      try {
        await Axios.post(
          `${this.apiBase}/${this.targetItem.id}/archive`,
          {},
          { headers: this.request_headers() }
        );
        this.showPopup("success", "تم الأرشفة بنجاح");
        this.archiveDialog = false;
        this.fetchReferrals();
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ");
      } finally {
        this.actionLoading = false;
      }
    },
    openRestoreDialog(item) {
      this.targetItem = item;
      this.restoreDialog = true;
    },
    async confirmRestore() {
      this.actionLoading = true;
      try {
        await Axios.post(
          `${this.apiBase}/${this.targetItem.id}/restore`,
          {},
          { headers: this.request_headers() }
        );
        this.showPopup("success", "تم الإستعادة بنجاح");
        this.restoreDialog = false;
        this.fetchReferrals();
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ");
      } finally {
        this.actionLoading = false;
      }
    },
    openDeleteDialog(item) {
      this.targetItem = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.actionLoading = true;
      try {
        await Axios.post(
          `${this.apiBase}/${this.targetItem.id}/delete`,
          {},
          { headers: this.request_headers() }
        );
        this.showPopup("success", "تم الحذف نهائياً بنجاح");
        this.deleteDialog = false;
        this.fetchReferrals();
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ");
      } finally {
        this.actionLoading = false;
      }
    },
    openBulkArchiveDialog() {
      if (!this.selected.length) return;
      this.bulkArchiveDialog = true;
    },
    async confirmBulkArchive() {
      this.actionLoading = true;
      try {
        const body = new FormData();
        this.selected.forEach((item, i) => {
          body.append(`ids[${i}]`, item.id);
        });
        await Axios.post(`${this.apiBase}/bulk-archive`, body, {
          headers: this.request_headers(),
        });
        this.showPopup("success", "تم أرشفة الروابط المحددة");
        this.bulkArchiveDialog = false;
        this.selected = [];
        this.fetchReferrals();
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ");
      } finally {
        this.actionLoading = false;
      }
    },
    copyUrl(url, label) {
      if (!url) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          () => this.showPopup("success", `تم نسخ ${label}`),
          () => this.showPopup("error", "تعذر النسخ")
        );
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        try {
          document.execCommand("copy");
          this.showPopup("success", `تم نسخ ${label}`);
        } catch (e) {
          this.showPopup("error", "تعذر النسخ");
        }
        document.body.removeChild(input);
      }
    },
  },
  created() {
    this.fetchMeta();
    this.fetchReferrals();
  },
};
</script>

<style scoped>
.referrals-crud {
  padding: 16px;
}
</style>
